// Utility functions for formatting plain text content from Airtable
// This allows CMS team to use plain text without HTML knowledge

export const formatPlainTextContent = (plainText) => {
  if (!plainText) return '';
  
  // Convert plain text to formatted content
  const paragraphs = plainText.split('\n\n').filter(p => p.trim());
  
  return paragraphs.map((paragraph, index) => {
    const trimmed = paragraph.trim();
    
    // Skip empty paragraphs
    if (!trimmed) return null;
    
    // Handle different paragraph types based on content
    if (trimmed.startsWith('# ')) {
      // Main heading (remove # and format as h1)
      return (
        <h1 key={index} style={{
          fontSize: 'clamp(28px, 4vw, 36px)',
          fontWeight: '700',
          color: '#1a1a1a',
          marginBottom: '24px',
          lineHeight: '1.2',
          fontFamily: 'Inter, sans-serif'
        }}>
          {trimmed.substring(2)}
        </h1>
      );
    }
    
    if (trimmed.startsWith('## ')) {
      // Section heading (remove ## and format as h2)
      return (
        <h2 key={index} style={{
          fontSize: 'clamp(22px, 3vw, 28px)',
          fontWeight: '600',
          color: '#1a1a1a',
          marginTop: '32px',
          marginBottom: '16px',
          lineHeight: '1.3',
          fontFamily: 'Inter, sans-serif'
        }}>
          {trimmed.substring(3)}
        </h2>
      );
    }
    
    if (trimmed.startsWith('### ')) {
      // Sub-heading (remove ### and format as h3)
      return (
        <h3 key={index} style={{
          fontSize: 'clamp(18px, 2.5vw, 22px)',
          fontWeight: '600',
          color: '#1a1a1a',
          marginTop: '24px',
          marginBottom: '12px',
          lineHeight: '1.4',
          fontFamily: 'Inter, sans-serif'
        }}>
          {trimmed.substring(4)}
        </h3>
      );
    }
    
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
      // List item
      return (
        <div key={index} style={{
          display: 'flex',
          alignItems: 'flex-start',
          marginBottom: '8px',
          paddingLeft: '20px'
        }}>
          <span style={{
            color: '#007bff',
            fontWeight: 'bold',
            marginRight: '12px',
            marginTop: '2px'
          }}>•</span>
          <p style={{
            fontSize: 'clamp(14px, 2vw, 16px)',
            lineHeight: '1.6',
            color: '#4a4a4a',
            margin: '0',
            fontFamily: 'Inter, sans-serif'
          }}>
            {trimmed.substring(2)}
          </p>
        </div>
      );
    }
    
    // Regular paragraph
    return (
      <p key={index} style={{
        fontSize: 'clamp(14px, 2vw, 16px)',
        lineHeight: '1.6',
        color: '#4a4a4a',
        marginBottom: '16px',
        fontFamily: 'Inter, sans-serif'
      }}>
        {trimmed}
      </p>
    );
  }).filter(Boolean);
};

export const formatPlainTextExcerpt = (plainText, maxLength = 150) => {
  if (!plainText) return '';
  
  // Remove any markdown-style formatting for excerpt
  const cleaned = plainText
    .replace(/^#+\s+/gm, '') // Remove heading markers
    .replace(/^[-•]\s+/gm, '') // Remove list markers
    .replace(/\n+/g, ' ') // Replace line breaks with spaces
    .trim();
  
  // Truncate to maxLength
  if (cleaned.length <= maxLength) {
    return cleaned;
  }
  
  return cleaned.substring(0, maxLength).trim() + '...';
};

export const formatPlainTextTitle = (plainText) => {
  if (!plainText) return '';
  
  // Extract title from content if it starts with heading
  if (plainText.startsWith('# ')) {
    const lines = plainText.split('\n');
    return lines[0].substring(2).trim();
  }
  
  return plainText.trim();
};

// Helper function to convert existing HTML content to plain text format
export const convertHtmlToPlainText = (htmlContent) => {
  if (!htmlContent) return '';
  
  return htmlContent
    // Convert HTML headings to markdown-style
    .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n')
    .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n')
    .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n')
    // Convert paragraphs
    .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n')
    // Convert list items
    .replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1\n')
    // Remove remaining HTML tags
    .replace(/<[^>]*>/g, '')
    // Clean up extra whitespace
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};
